import { CSSProperties, FC, PropsWithChildren, ReactNode } from 'react'

import { Col, ConfigProvider, List, Row, theme } from 'antd'

import classNames from 'classnames'
import sanitizeHtml from 'sanitize-html'

import Text from '..//text/Text'
import { useColor } from '../app'
import IvyIcon from '../icon'
import { Avatar } from '../image/Avatar'

const { useToken } = theme

type ListItemProps = PropsWithChildren<
  Partial<
    {
      className: string
      size: 'small' | 'middle' | 'large'
      bordered: boolean
      gutter?: number
      onClick: () => void
    } & typeof List.Item.defaultProps
  >
>

const ListItem: FC<ListItemProps> = ({
  className = '',
  size,
  gutter,
  bordered = false,
  onClick,
  children,
  ...props
}) => {
  const {
    token: { colorBorder, borderRadius },
  } = useToken()
  const padding = gutter ?? (size === 'small' ? 8 : size === 'middle' ? 12 : 24) / 2

  return (
    <List.Item
      className={classNames(className, onClick ? 'hoverable' : '')}
      style={{
        margin: `${bordered ? '16px 0' : '0'}`,
        borderRadius,
        borderBottom: bordered ? `1px solid ${colorBorder}` : 'none',
        padding: `${padding}px ${bordered ? padding : 0}px`,
        cursor: onClick && 'pointer',
      }}
      onClick={onClick}
      {...props}
    >
      {children}
    </List.Item>
  )
}

export type ListItemMetaProps = {
  onClick?: () => void
  className?: string
  gutter?: number | [number, number]
  avatar?: SDK.Components.AvatarProps
  color?: SDK.Components.ColorVariant
  icon?: Design.IvyIcon
  title?: ReactNode
  imagePlacement?: 'left' | 'right'
  subTitle?: ReactNode
  description?: ReactNode
  extra?: ReactNode
  style?: CSSProperties
  image?: FC
  info?: ReactNode
  size?: 'small' | 'middle' | 'large'
}

const ListItemMeta: FC<PropsWithChildren<ListItemMetaProps>> = ({
  onClick,
  gutter = [8, 8],
  className,
  image: DefaultImageComponent,
  imagePlacement = 'left',
  children,
  avatar: {
    icon: avatarIcon,
    src,
    shape = 'square',
    size: avatarSize = 48,
    placeholder,
    background,
    text,
    ...avatarProps
  } = {},
  icon,
  title,
  color = 'default',
  description = undefined,
  extra,
  subTitle,
  style,
  info,
  size = 'medium',
}) => {
  const { textByVariant } = useColor()

  const iconSize = size === 'small' ? avatarSize / 3 : size === 'medium' ? avatarSize / 2.4 : avatarSize
  const ImageComponent = () => {
    if (DefaultImageComponent) return <DefaultImageComponent />
    if (avatarProps?.children) return avatarProps.children
    if (!icon && !avatarIcon && !src && !text && !placeholder) return null
    if (size === 'small' && icon) return <IvyIcon size={iconSize} color={'primary'} type={icon} />
    if (icon && !src) return <Avatar background={background} icon={icon} shape={shape} size={avatarSize}></Avatar>

    return (
      <Avatar
        src={!text ? src : undefined}
        size={avatarSize}
        placeholder={placeholder}
        icon={!text && avatarIcon}
        text={text}
        shape={shape}
        background={background}
        {...avatarProps}
      />
    )
  }

  return (
    <Row
      align={'top'}
      justify={imagePlacement === 'left' ? 'start' : 'end'}
      style={{
        width: '100%',
        color: textByVariant(color),
        cursor: onClick && 'pointer',
        textAlign: imagePlacement,
        ...style,
      }}
      wrap={false}
      className={classNames(className, onClick ? 'hoverable' : '')}
      gutter={size === 'small' ? 8 : gutter}
      onClick={onClick}
    >
      <Col flex={'none'} order={imagePlacement === 'left' ? 1 : 2}>
        <ImageComponent />
      </Col>
      <Col flex={'auto'} order={imagePlacement === 'left' ? 2 : 1}>
        <Row justify={'space-between'} wrap={false}>
          <Col flex={'auto'} order={imagePlacement === 'left' ? 1 : 2}>
            <Row style={{ width: '100%' }} gutter={[0, 2]} justify={'space-between'} align={'top'}>
              {title && typeof title === 'string' ? (
                <Col span={24}>
                  <Text
                    type={color}
                    strong
                    translate={'no'}
                    style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                  >
                    {title}
                  </Text>
                </Col>
              ) : (
                title
              )}
              {subTitle && (
                <Col span={24}>
                  {typeof subTitle === 'string' ? (
                    <Text strong type={'secondary'} translate={'no'}>
                      {subTitle}
                    </Text>
                  ) : (
                    subTitle
                  )}
                </Col>
              )}
              {description && (
                <Col span={24}>
                  {typeof description === 'string' ? (
                    <Text translate={'no'} rows={2} type={'secondary'}>
                      {sanitizeHtml(description, { allowedTags: [] })}
                    </Text>
                  ) : (
                    description
                  )}
                </Col>
              )}
            </Row>
          </Col>
          <Col
            flex={'none'}
            style={{ alignItems: 'flex-end', textAlign: 'right' }}
            order={imagePlacement === 'left' ? 2 : 1}
          >
            <Row align={'bottom'} gutter={[0, 2]}>
              <Col span={24}>{typeof extra === 'string' ? <Text type={'secondary'}>{extra}</Text> : extra}</Col>
              <Col span={24}>
                <ConfigProvider componentSize={'small'}>
                  {typeof info === 'string' ? (
                    <Text translate={'no'} type={'secondary'}>
                      {info}
                    </Text>
                  ) : (
                    info
                  )}
                </ConfigProvider>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col span={24}>{children}</Col>
        </Row>
      </Col>
    </Row>
  )
}

export { ListItem, ListItemMeta }
